import { Gradient, Hacker } from "@paulie/ui";
import { Component } from "solid-js";
import { ORCHARD_URL } from "../config";
import { Background } from "../components/Background";
import { NavCard } from "../components/Card";
import { IconArrowDown } from "../components/Common";
import { Header } from "../components/Header";
import { Layout } from "../components/Layout";
import * as Typography from "../components/Typography";

const ScrollArrowsIcon = (props: { class?: string }) => (
  <svg
    class={props.class}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      stroke-linecap="round"
      stroke-linejoin="round"
      stroke-width={2}
      d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
    />
  </svg>
);

const LayoutGridIcon = (props: { class?: string }) => (
  <svg
    class={props.class}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      stroke-linecap="round"
      stroke-linejoin="round"
      stroke-width={2}
      d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
    />
  </svg>
);

/**
 * Home Page Component
 *
 * The main landing page for paulie.app with animated text effects
 * and links to demo pages. Features a full-viewport hero section
 * with scroll indicator, and Interactive Demos section below the fold.
 */
const Home: Component = () => {
  return (
    <div class="text-whitesmoke scroll-smooth font-mono">
      <Background />
      <Layout header={<Header />}>
        {/* Hero Section - Full Viewport */}
        <div class="relative flex h-screen flex-col items-center justify-center">
          <Typography.Heading>
            <div>
              <Hacker>Welcome to</Hacker>
            </div>
            <div>
              <Gradient>paulie.app</Gradient>
            </div>
          </Typography.Heading>

          {/* Scroll Indicator Chevrons */}
          <div class="absolute bottom-12 left-1/2 flex -translate-x-1/2 animate-bounce flex-col items-center gap-2">
            <div class="mb-2 text-xs text-gray-400">Scroll to explore</div>
            <IconArrowDown class="h-6 w-6 text-purple-400/60" />
          </div>
        </div>

        {/* Interactive Demos Section - Below the Fold */}
        <div class="flex min-h-screen items-center py-20">
          <div class="relative z-10 mx-auto w-full max-w-2xl px-3 sm:px-6">
            <div class="space-y-6 rounded-2xl border border-white/10 bg-gray-900/60 p-4 backdrop-blur-sm sm:p-8">
              <h2 class="text-whitesmoke text-center text-2xl font-bold">
                Interactive Demos
              </h2>

              <div class="space-y-4">
                <NavCard
                  href={ORCHARD_URL}
                  icon={<span class="text-3xl">💾</span>}
                  title="Project ORCHARD"
                  description="An interactive fiction set inside Polus Strategic Industries, plus the games on its desktops"
                  tags={[
                    {
                      label: "Data Mining",
                      class: "border-cyan-500/30 bg-cyan-500/20 text-cyan-300",
                    },
                    {
                      label: "Word Games",
                      class:
                        "border-fuchsia-500/30 bg-fuchsia-500/20 text-fuchsia-300",
                    },
                    {
                      label: "Puzzles",
                      class: "border-cyan-500/30 bg-cyan-500/20 text-cyan-300",
                    },
                  ]}
                  cardClass="border-cyan-500/30 bg-gradient-to-r from-cyan-600/20 to-fuchsia-600/20 hover:border-cyan-400/50 hover:from-cyan-600/30 hover:to-fuchsia-600/30"
                  titleClass="text-cyan-300 group-hover:text-cyan-200"
                  chevronClass="text-cyan-400"
                />

                <NavCard
                  href="https://apps.paulie.app"
                  external
                  icon={<span class="text-3xl">🚀</span>}
                  title="Projects"
                  description="Developer tools, CLI utilities, and open source projects"
                  tags={[
                    {
                      label: "CLI Tools",
                      class:
                        "border-emerald-500/30 bg-emerald-500/20 text-emerald-300",
                    },
                    {
                      label: "Dev Tools",
                      class: "border-teal-500/30 bg-teal-500/20 text-teal-300",
                    },
                    {
                      label: "AI Agents",
                      class:
                        "border-emerald-500/30 bg-emerald-500/20 text-emerald-300",
                    },
                  ]}
                  cardClass="border-emerald-500/30 bg-gradient-to-r from-emerald-600/20 to-teal-600/20 hover:border-emerald-400/50 hover:from-emerald-600/30 hover:to-teal-600/30"
                  titleClass="text-emerald-300 group-hover:text-emerald-200"
                  chevronClass="text-emerald-400"
                />

                <NavCard
                  href="/demos/scroll-driven"
                  icon={<ScrollArrowsIcon class="h-8 w-8 text-purple-400" />}
                  title="Scroll-Driven Animations"
                  description="CSS animation-timeline demos that respond to scroll position"
                  tags={[
                    {
                      label: "4 Demos",
                      class:
                        "border-purple-500/30 bg-purple-500/20 text-purple-300",
                    },
                    {
                      label: "Parallax",
                      class:
                        "border-purple-500/30 bg-purple-500/20 text-purple-300",
                    },
                    {
                      label: "3D Effects",
                      class:
                        "border-purple-500/30 bg-purple-500/20 text-purple-300",
                    },
                    {
                      label: "Reveals",
                      class:
                        "border-purple-500/30 bg-purple-500/20 text-purple-300",
                    },
                  ]}
                  cardClass="border-purple-500/30 bg-gradient-to-r from-purple-600/20 to-blue-600/20 hover:border-purple-400/50 hover:from-purple-600/30 hover:to-blue-600/30"
                  titleClass="text-purple-300 group-hover:text-purple-200"
                  chevronClass="text-purple-400"
                />

                <NavCard
                  href="/demos/view-transitions"
                  icon={<LayoutGridIcon class="h-8 w-8 text-indigo-400" />}
                  title="View Transition Demos"
                  description="Modern API demos that respond to state changes and DOM updates"
                  tags={[
                    {
                      label: "1 Demo",
                      class:
                        "border-indigo-500/30 bg-indigo-500/20 text-indigo-300",
                    },
                    {
                      label: "Morphing",
                      class:
                        "border-indigo-500/30 bg-indigo-500/20 text-indigo-300",
                    },
                    {
                      label: "State Transitions",
                      class:
                        "border-indigo-500/30 bg-indigo-500/20 text-indigo-300",
                    },
                  ]}
                  cardClass="border-indigo-500/30 bg-gradient-to-r from-indigo-600/20 to-purple-600/20 hover:border-indigo-400/50 hover:from-indigo-600/30 hover:to-purple-600/30"
                  titleClass="text-indigo-300 group-hover:text-indigo-200"
                  chevronClass="text-indigo-400"
                />
              </div>

              <div class="pt-4 text-center text-xs text-gray-500">
                Built with SolidJS, Vite, and Tailwind CSS v4
              </div>
            </div>
          </div>
        </div>
      </Layout>
    </div>
  );
};

export default Home;
